import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useProfileSync } from '@/hooks/useAuth';
import { BootScreen } from '@/components/common/ShellBoot';

interface Props {
  children: ReactNode;
}

interface GuardState {
  isAuthenticated: boolean;
  role: ReturnType<typeof useAuthStore.getState>['role'];
  /** 프로필을 받아오는 중 */
  isLoadingProfile: boolean;
}

/**
 * 두 가드가 공유하는 상태.
 *
 * 여기서 `useProfileSync()` 를 부르는 것이 새로고침 직후 `GET /auth/me` 가
 * 한 번 도는 유일한 지점이다. 저장소 마이그레이션(v2)이 토큰만 남기고 프로필을
 * 비우기 때문에, 이걸 빼면 사이드바·탭 권한이 빈 채로 그려진다.
 */
const useGuardState = (): GuardState => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const role = useAuthStore((state) => state.role);
  const profile = useProfileSync();

  return { isAuthenticated, role, isLoadingProfile: profile.isLoading };
};

/**
 * 관리자·뷰어 전용 구역.
 *
 * 기관 계정은 여기서 되돌려 보내지므로 관리자 번들(`children` 안의 lazy 컴포넌트)을
 * **다운로드조차 하지 않는다.** React.lazy 는 실제로 렌더될 때 청크를 받으므로,
 * 리다이렉트가 먼저 일어나면 요청 자체가 나가지 않는다.
 */
export const AdminRoute = ({ children }: Props) => {
  const { isAuthenticated, role, isLoadingProfile } = useGuardState();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // 토큰만 있고 역할을 아직 모르는 상태. 잘못 보내면 엉뚱한 화면이 잠깐 뜬다
  if (!role) {
    return isLoadingProfile ? (
      <BootScreen message='계정 정보를 확인하는 중...' />
    ) : (
      <Navigate to='/login' replace />
    );
  }

  if (role === 'institution') {
    return <Navigate to='/portal' replace />;
  }

  return <>{children}</>;
};

/** 기관 담당자 전용 구역. 관리자·뷰어는 관리자 첫 화면으로 되돌린다 */
export const InstitutionRoute = ({ children }: Props) => {
  const { isAuthenticated, role, isLoadingProfile } = useGuardState();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  if (!role) {
    return isLoadingProfile ? (
      <BootScreen message='계정 정보를 확인하는 중...' />
    ) : (
      <Navigate to='/login' replace />
    );
  }

  if (role !== 'institution') {
    return <Navigate to='/kiosks' replace />;
  }

  return <>{children}</>;
};
